"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import posthog from "posthog-js";

/**
 * Celebratory confirmation shown when the board loads with `?alert=created`
 * — i.e. right after the double-opt-in link created the alert. It's the last
 * step of the alert flow: tell the user it worked, what it's scoped to, and
 * where to manage it, then let them keep browsing. Auto-opens once on mount
 * and strips the `alert` param on close so a refresh never re-triggers it.
 */
export function AlertConfirmedModal({
  scopeLabel,
  email,
}: {
  /** Human summary of the saved search, e.g. '“product manager” + 2 filters'. */
  scopeLabel: string;
  /** The address the daily digest goes to, when known. */
  email?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  useEffect(() => {
    dialogRef.current?.showModal();
    if (posthog.__loaded) posthog.capture("job_alert_confirmed", { method: "signed_in" });
  }, []);

  /** Close + drop the one-shot `alert` param so refresh/back won't re-open it. */
  const dismiss = () => {
    dialogRef.current?.close();
    const next = new URLSearchParams(sp.toString());
    next.delete("alert");
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  return (
    <dialog
      ref={dialogRef}
      className="pitch-dialog"
      aria-labelledby="alert-confirmed-title"
      onClick={(e) => {
        if (e.target === dialogRef.current) dismiss();
      }}
      onCancel={(e) => {
        e.preventDefault();
        dismiss();
      }}
    >
      <div className="overflow-hidden rounded-xl border-2 border-ink bg-white">
        <div className="p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <p className="telex flex items-center gap-2 text-[9.5px] text-emerald-700">
              <span aria-hidden className="inline-block h-[6px] w-[6px] bg-current" />
              Alert confirmed
            </p>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-full text-lg leading-none text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              ×
            </button>
          </div>

          <h2
            id="alert-confirmed-title"
            className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink"
          >
            You&rsquo;re all set.
          </h2>

          <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
            Your daily alert for <span className="font-medium text-ink">{scopeLabel}</span>{" "}
            is on. We&rsquo;ll email {email ? <span className="font-medium text-ink">{email}</span> : "you"}{" "}
            fresh matches every morning — never any spam.
          </p>

          <div className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 px-3.5 py-3 text-[13.5px] leading-relaxed text-neutral-600">
            Manage or pause your alerts anytime from your{" "}
            <Link
              href="/dashboard"
              className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
            >
              dashboard
            </Link>
            .
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
          >
            Keep browsing jobs
          </button>
        </div>
      </div>
    </dialog>
  );
}
